<template>
    <div class="JsonSchemaEnumForm" :class="{ 'JsonSchemaEnumForm--focused': focused }">
        <FormElement :label-for="id" :label="form.schema.title" :required="required">
            <template #requiredIndicator>
                {{ translate('requiredLabel') }}
            </template>
            <template #labelHint>
                <div v-if="form.schema.warning" class="JsonSchemaForm__warning">
                    {{ form.schema.warning }}
                </div>
                <div v-else-if="form.schema.hint" class="JsonSchemaForm__hint">
                    {{ form.schema.hint }}
                </div>
            </template>

            <FormInput :invalid="accessed && hasError" force-error-slot force-action-slot>
                <NewDropdown
                    :id="id"
                    type="text"
                    :disabled="disabled"
                    :selected="viewValue"
                    :options="options"
                    :placeholder="'placeholder' in form.schema && form.schema.placeholder"
                    :maxlength="'maxLength' in form.schema && form.schema.maxLength || 1024"
                    @input="onChange"
                    @blur="touch"
                    @focus="onFocus"
                />

                <template v-if="accessed && error" #errorMessage>
                    {{ error }}
                </template>

                <button v-if="!required && viewValue !== null" class="JsonSchemaEnumForm__clear" @click="clearValue">
                    <FontAwesomeIcon :icon="faXmark" /> <span class="JsonSchemaEnumForm__clearText">Clear</span>
                </button>

                <template v-if="deletable" #actionButtons>
                    <NewButton
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <FontAwesomeIcon :icon="faXmark" />
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { Component } from 'vue-property-decorator';

    import { faAngleDown, faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';

    import NewDropdown from '../../Dropdown/NewDropdown.vue';
    import { DropdownOption } from '../../Dropdown/types';
    import { FormElement, FormInput } from '../../Form';
    import NewButton from '../../new-button/NewButton.vue';
    import { Select } from '../../Select';

    import AbstractJsonSchemaForm from './AbstractJsonSchemaForm';

    @Component({
        emits: [ 'delete', 'input' ],
        components: { FormInput, FormElement, Select, FontAwesomeIcon, NewDropdown, NewButton },
        inheritAttrs: false,
    })
    export default class JsonSchemaEnumForm extends AbstractJsonSchemaForm<JsonFormBase<JsonSchemaEnum>, string> {
        private faXmark = faXmark;
        private faAngleDown = faAngleDown;

        private get viewValue(): { label: string, value: any } | null {
            const index = this.form.schema.enum.indexOf(this.value);
            return index === -1 ? null : { label: this.form.schema.enumLabels?.[index] || `${this.value}`, value: this.value };
        }

        private get options(): { label: string, value: any }[] {
            return this.form.schema.enum.map((option, index) => ({
                label: this.form.schema.enumLabels?.[index] || `${option}`,
                value: option,
            }));
        }

        private buildLabel(option: any, index: number): string {
            return this.form.schema.enumLabels?.[index] || `${option}`;
        }

        private onChange(input: DropdownOption): void {
            const inputValue = input.value;
            this.$emit('input', this.form.getValue(inputValue));
        }

        private clearValue() {
            this.$emit('input', this.form.getValue(undefined));
        }
    }
</script>
